"use client";

import React, { useEffect, useState } from "react";

import * as S from "./DealTermsTableLayout.style";
import {
  TableContainer,
  TableRowContainer,
  TableShellTitle,
  TableShellValue,
} from "../../../shared/components/core";
import TableGridColumnLayout from "../TableGridColumnLayout/TableGridColumnLayout";

type TableItem = {
  title: React.ReactNode | string;
  value: React.ReactNode;
};

type TableCell = TableItem[] | TableItem;
type TableColumn = TableCell[];

interface Props {
  items: TableColumn[];
  isEditable?: boolean;
  mobileColumnCount?: number;
}

const VALUE_SHELL_PADDING = "0.2rem";
const getRowCount = (columns: TableColumn[]) =>
  Math.max(...columns.map((column) => column?.length || 0), 0);

const splitColumns = (items: TableColumn[], columnCount: number) => {
  if (columnCount <= 0) return [items];

  const chunks: TableColumn[][] = [];

  for (let index = 0; index < items.length; index += columnCount) {
    const chunk = items.slice(index, index + columnCount);

    chunks.push(
      items.length > columnCount && chunk.length < columnCount
        ? [
            ...chunk,
            ...Array.from(
              { length: columnCount - chunk.length },
              (): TableColumn => [],
            ),
          ]
        : chunk,
    );
  }

  return chunks;
};

const DealTermsTableLayout = ({
  items,
  isEditable = false,
  mobileColumnCount,
}: Props) => {
  const columnCount = items?.length || 0;
  const rowCount = getRowCount(items);

  const windowWidth = window.innerWidth;
  const [currentWindowWidth, setCurrentWindowWidth] = useState(windowWidth);

  useEffect(() => {
    const changeCurrentWindowWidth = () => {
      const currenWidth = window.innerWidth;
      setCurrentWindowWidth(currenWidth);
    };
    changeCurrentWindowWidth();
    window.addEventListener("resize", changeCurrentWindowWidth);
    return () => window.removeEventListener("resize", changeCurrentWindowWidth);
  }, []);

  const isMobileScreen = currentWindowWidth < 767;

  if (!columnCount || !rowCount) return null;

  const columnGroups =
    isMobileScreen && mobileColumnCount !== undefined
      ? splitColumns(items, mobileColumnCount)
      : [items];
  const valuePadding = isEditable ? VALUE_SHELL_PADDING : "0.8rem";

  const renderTableContent = (tableItem: TableCell, itemKey: string) => {
    if (Array.isArray(tableItem)) {
      const hasTitle = tableItem.at(0)?.title;

      return (
        <>
          {!isMobileScreen && hasTitle && (
            <TableRowContainer>
              {tableItem.map((item, index) => (
                <TableShellTitle key={`${itemKey}-title-${String(index)}`}>
                  {item.title}
                </TableShellTitle>
              ))}
            </TableRowContainer>
          )}

          {isMobileScreen && hasTitle && (
            <TableRowContainer>
              <TableShellTitle>
                {tableItem.map((item, index) => (
                  <React.Fragment
                    key={`${itemKey}-mobile-title-${String(index)}`}
                  >
                    {index > 0 && " & "}
                    {item.title}
                  </React.Fragment>
                ))}
              </TableShellTitle>
            </TableRowContainer>
          )}

          {!isMobileScreen && (
            <TableRowContainer className="deal-terms-table-value">
              {tableItem.map((subItem, index) => (
                <TableShellValue
                  key={`${itemKey}-desktop-value-${String(index)}`}
                  padding={valuePadding}
                >
                  {subItem?.value || ""}
                </TableShellValue>
              ))}
            </TableRowContainer>
          )}

          {isMobileScreen && (
            <TableRowContainer style={{ backgroundColor: "#f8f9fa" }}>
              <TableShellValue padding={valuePadding}>
                {tableItem.map((item, index) => (
                  <div
                    className={`value-index-${index}`}
                    key={`${itemKey}-mobile-value-${String(index)}`}
                  >
                    {item.value}
                  </div>
                ))}
              </TableShellValue>
            </TableRowContainer>
          )}
        </>
      );
    }

    return (
      <>
        <TableRowContainer>
          <TableShellTitle>{tableItem?.title || ""}</TableShellTitle>
        </TableRowContainer>
        <TableRowContainer className="deal-terms-table-value">
          <TableShellValue padding={valuePadding}>
            {tableItem?.value}
          </TableShellValue>
        </TableRowContainer>
      </>
    );
  };

  return (
    <S.ContentsWrapper>
      {columnGroups.map((columnGroup, groupIndex) => {
        const groupColumnCount = columnGroup.length;
        const groupRowCount = getRowCount(columnGroup);

        return (
          <TableGridColumnLayout
            columnCount={groupColumnCount}
            key={`deal-terms-table-group-${String(groupIndex)}`}
          >
            {Array(groupRowCount)
              .fill("ROW")
              .map((_, rowIndex) => (
                <React.Fragment
                  key={`item-row-${String(groupIndex)}-${String(rowIndex)}`}
                >
                  {Array(groupColumnCount)
                    .fill("COLUMN")
                    ?.map((__, columnIndex) => {
                      const tableItem = columnGroup[columnIndex]?.[rowIndex];
                      const itemKey = `item-shell-${String(
                        groupIndex,
                      )}-${String(columnIndex)}-${String(rowIndex)}`;

                      return tableItem ? (
                        <TableContainer
                          className={`${"default-table-container"} ${
                            rowIndex === 0 && "top-radius"
                          } ${
                            rowIndex === columnGroup[columnIndex].length - 1 &&
                            "bottom-radius"
                          }`}
                          key={itemKey}
                        >
                          {renderTableContent(tableItem, itemKey)}
                        </TableContainer>
                      ) : (
                        <div key={itemKey} />
                      );
                    })}
                </React.Fragment>
              ))}
          </TableGridColumnLayout>
        );
      })}
    </S.ContentsWrapper>
  );
};

export default DealTermsTableLayout;
